'use client';

import {useI18n} from '@udemy-v2/i18n';
import {TextSkeleton} from '@udemy-v2/react-reveal-components';
import React from 'react';

import {BuyBox} from '@/features/course-landing-page/components/buy-box/buy-box.react-component';
import {
    useClpPurchaseContext,
    CLPPurchaseOption,
} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {SubscriptionPurchaseContent} from '@/features/course-landing-page/components/subscription-purchase-content/subscription-purchase-content.react-component';
import styles from './marketplace-purchase-content.module.scss';
import {CourseContentPlacement} from '@/features/course-landing-page/course-landing-page.react-component';

export interface MarketplacePurchaseContentProps {
    placement: CourseContentPlacement;
    premiumCourseNotice?: React.ReactNode;
}

/**
 * This component renders the marketplace purchase options for subscription-eligible courses
 * @param placement
 * @param premiumCourseNotice
 * @constructor
 */
export const SubscriptionMarketplacePurchaseContent = ({
    placement,
    premiumCourseNotice,
}: MarketplacePurchaseContentProps) => {
    const {gettext} = useI18n();
    const {isLoading, isCourseInAvailablePlans, selectedPurchaseOption} = useClpPurchaseContext();

    if (isLoading) {
        return (
            <div className={styles.container}>
                <TextSkeleton
                    className={styles.skeleton}
                    withTitle={true}
                    lineCountPerParagraph={3}
                    paragraphCount={2}
                />
                <div className={styles.separator}>{gettext('or')}</div>
                <TextSkeleton
                    className={styles.skeleton}
                    withTitle={false}
                    lineCountPerParagraph={2}
                    paragraphCount={1}
                />
            </div>
        );
    }

    // Determine primary and secondary content based on selected purchase option
    const isSubscriptionPrimary = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;

    const subscriptionContent = isCourseInAvailablePlans && <SubscriptionPurchaseContent />;

    const transactionalContent = <BuyBox placement={placement} />;

    const primaryContent = isSubscriptionPrimary ? subscriptionContent : transactionalContent;
    const secondaryContent = isSubscriptionPrimary ? transactionalContent : subscriptionContent;

    return (
        <div className={styles.container}>
            {premiumCourseNotice && premiumCourseNotice}
            {primaryContent}
            {secondaryContent && (
                <>
                    <div className={styles.separator}>{gettext('or')}</div>
                    {secondaryContent}
                </>
            )}
        </div>
    );
};
